import type { Temporal } from "@js-temporal/polyfill";
import {
  DropdownPanel,
  Icon,
  MenuOption,
  MenuOptionWrapper,
} from "@smartsuite/react-ui/lib";
import { getNextCallsForSchedule } from "@smartsuite/automation-schedule";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { useRootStore } from "../../../../../../../../../../stores/hooks";
import { getTimeFormatByUserPreferences } from "../../../../../../../../../../utils/date";
import { epochSecondsOf } from "../../../../../../../../../../utils/temporal";
import * as S from "./style";
import { GrayColors, TextColorNames } from "@smartsuite/types";

export const NextCallsInfo = observer(() => {
  const link = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState<boolean>(false);
  const { config, automations, scheduleStore } = useRootStore();

  const [nextCalls, setNextCalls] = useState<Temporal.ZonedDateTime[]>([]);

  const getTimezone = (): string => automations.candidate.getTimezone();

  const format = (unixTime: number): string => {
    const date = moment(unixTime * 1000).tz(getTimezone());
    return (
      date.format("MMM DD") +
      " at " +
      date
        .format(getTimeFormatByUserPreferences(config.userPreferences))
        .toUpperCase()
    );
  };

  const formatInnerDates = (unixTime: number): string => {
    const date = moment(unixTime * 1000).tz(getTimezone());
    return (
      date.format("DD MMMM YYYY") +
      " at " +
      date
        .format(getTimeFormatByUserPreferences(config.userPreferences))
        .toUpperCase()
    );
  };

  useEffect(() => {
    if (scheduleStore.schedule === undefined) {
      return;
    }

    setNextCalls(
      getNextCallsForSchedule(scheduleStore.schedule, {
        timezone: getTimezone(),
        count: 10,
      }),
    );
  }, [scheduleStore.schedule]);

  if (nextCalls.length === 0) {
    return null;
  }

  return (
    <S.Container>
      <Icon name="info-baloon" size="S" color={GrayColors["G-4"]} />
      <S.Text>
        {t("scheduled-trigger.execute-note")}{" "}
        {format(epochSecondsOf(nextCalls[0]))}.
      </S.Text>
      <S.Link
        ref={link}
        color={opened ? TextColorNames.primary : TextColorNames.secondary}
        onClick={() => {
          setOpened(!opened);
        }}
        onMouseOver={() => {
          setOpened(true);
        }}
      >
        {t("scheduled-trigger.show-next-10-runs")}
      </S.Link>
      <DropdownPanel
        targetElementRef={link}
        isActive={opened}
        my="center top"
        at="center top+20"
        onClickOutside={() => setOpened(false)}
      >
        <MenuOptionWrapper>
          <S.MenuTitle>{t("scheduled-trigger.next-10-runs")}</S.MenuTitle>
          {nextCalls.map((nextCall) => (
            <MenuOption key={epochSecondsOf(nextCall)}>
              <S.MenuText>
                {formatInnerDates(epochSecondsOf(nextCall))}
              </S.MenuText>
            </MenuOption>
          ))}
        </MenuOptionWrapper>
      </DropdownPanel>
    </S.Container>
  );
});
